import dayjs from "dayjs";

import Polygon, { circular } from "ol/geom/Polygon";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";

import {
    getAsdFeatureStyleFromLayersAndSettings,
    getDpzCoordinatesFromTrack,
    getGeometryStyleFromFeatureAndSettings,
    getPredLineCoordinatesFromTrackAndSettings
} from "../map/mapUtil";

import { ConvertMetersToFeet, ConvertMpsToFpm, ConvertMpsToKnots } from "../util";

export function getMinTimeFromPlaybackFile(data) {
    return new Date(data.time_start_iso_str).getTime();
}

export function getMaxTimeFromPlaybackFile(data) {
    return new Date(data.time_end_iso_str).getTime();
}

export function getPlaybackTimeValueLabelFromMs(time) {
    return dayjs(time).format("MMM Do h:mm:ss a");
}

export function getAsdFeaturesFromPlaybackFileAndTimestamp(events, timestamp, settings, user, layers, zcAlerts, avAlerts, previousAsdLocations) {
    const features = [];
    events.forEach((event, trackId) => {
        const updatedAsdMessage = getAdditionalAsdFields(event.message, previousAsdLocations, user);

        const eventTimestampMs = new Date(event.timestamp_iso_str).getTime();
        if (timestamp - eventTimestampMs < 10 * 1000) {
            const tracks = [
                { id: `${trackId}`, geometry: new Point([updatedAsdMessage.lon_deg, updatedAsdMessage.lat_deg]) },
                { id: `${trackId}_pl`, geometry: new LineString(getPredLineCoordinatesFromTrackAndSettings(updatedAsdMessage, settings)) },
                { id: `${trackId}_hl`, geometry: new LineString(updatedAsdMessage.history_locations) },
                { id: `${trackId}_dpz`, geometry: new Polygon(getDpzCoordinatesFromTrack(updatedAsdMessage, settings)) }
            ];
            tracks.forEach(({ id, geometry }, i) => {
                const newFeature = new Feature({
                    geometry: geometry
                });
                newFeature.setId(id);
                newFeature.set("asd", updatedAsdMessage);

                if (i === 0) {
                    if (updatedAsdMessage.callsign && updatedAsdMessage.callsign[user.organization_id]) {
                        newFeature.set("name", updatedAsdMessage.callsign[user.organization_id]);
                    } else {
                        newFeature.set("name", updatedAsdMessage.label);
                    }
                    const zeroConflictionAlerts = [];
                    zcAlerts.forEach((zeroConflictAlertEvent) => {
                        if (zeroConflictAlertEvent.message.state !== "ACTIVE") return;

                        const ownshipId = zeroConflictAlertEvent.message.ownship_track.id;
                        const intruderId = zeroConflictAlertEvent.message.intruder_track.id;

                        if (ownshipId === id || intruderId === id) {
                            zeroConflictionAlerts.push(zeroConflictAlertEvent.message);
                        }
                    });
                    newFeature.set("zero_confliction_alerts", zeroConflictionAlerts);

                    let activeAlert = undefined;
                    avAlerts.forEach((alertVolumeAlertEvent) => {
                        const alertEventTimestampMs = new Date(alertVolumeAlertEvent.message.time_of_alert).getTime();
                        const isWithinTenSeconds = timestamp - alertEventTimestampMs < 10000;

                        if (alertVolumeAlertEvent.message.track_id === updatedAsdMessage.id && isWithinTenSeconds) {
                            activeAlert = alertVolumeAlertEvent.message;
                        }
                    });
                    newFeature.set("alert_volume_confliction_alert", activeAlert);
                }
                const newFeatureStyle = getAsdFeatureStyleFromLayersAndSettings(newFeature, layers, settings, user);
                newFeature.setStyle(newFeatureStyle);
                features.push(newFeature);
            });
        }
    });
    return features;
}

export function getFlightVolumesFromPlaybackFileAndTimestamp(events, timestamp, settings, type) {
    const features = [];
    events.forEach((event, id) => {
        const timeEndMs = new Date(event.message.time_end).getTime();

        let deleted = false;
        let hasEnded = timeEndMs <= timestamp ? true : false;

        if (type === "FLIGHT" || type === "CONSTRAINT") {
            deleted = event.message.state !== "DELETED" ? false : true;
        } else if (type === "ALERT_VOLUME") {
            deleted = event.message.deleted === true ? true : false;

            if (event.message.permanent) {
                hasEnded = false;
            }
        }
        if (deleted === false && hasEnded === false) {
            const volumes = "volumes" in event.message ? event.message.volumes : event.message.polygons;
            if (!Array.isArray(volumes)) return [];

            volumes.forEach((volume, i) => {
                let geometry = undefined;
                if (volume.polygon) {
                    geometry = new Polygon([volume.polygon.map(({ lng, lat }) => [lng, lat])]);
                } else if (volume.type === "polygon") {
                    geometry = new Polygon([volume.vertices.map(({ lng, lat }) => [lng, lat])]);
                } else if (volume.circle) {
                    geometry = circular([volume.circle.center.lng, volume.circle.center.lat], volume.circle.radius_m, 128);
                } else if (volume.type === "circle") {
                    geometry = circular([volume.lng, volume.lat], volume.radius, 128);
                }
                const newFeature = new Feature({
                    geometry: geometry
                });
                newFeature.setId(`${id}_${i}`);
                newFeature.set(event.type.toLowerCase(), event.message);
                newFeature.set("name", event.message.name);

                const style = getGeometryStyleFromFeatureAndSettings(newFeature, settings);
                newFeature.setStyle(style);
                features.push(newFeature);
            });
        }
    });
    return features;
}
export function getPlaybackFileValid(data) {
    const startDateDayJs = dayjs(data.time_start_iso_str);
    const endDateDayJs = dayjs(data.time_end_iso_str);

    if (!data.time_start_iso_str || !data.time_end_iso_str || startDateDayJs.isAfter(endDateDayJs) || startDateDayJs.isAfter(dayjs())) {
        return "Please ensure your file provides valid start and end times, and that they are in the past.";
    }
    if (Array.isArray(data.events) === false || data.events.length <= 1) {
        return "Please ensure your file contains valid events that are sorted in ascending order.";
    }
    for (let i = 1; i < data.events.length; i++) {
        const currentTimestampStr = data.events[i].timestamp_iso_str;
        const previousTimestampStr = data.events[i - 1].timestamp_iso_str;

        const currentTimestamp = new Date(currentTimestampStr);
        const previousTimestamp = new Date(previousTimestampStr);

        if (currentTimestamp.getTime() < previousTimestamp.getTime()) {
            return "Please ensure your file contains valid events that are sorted in ascending order.";
        }
    }
    return "";
}

// function which computes additional fields
function getAdditionalAsdFields(message, previousAsdLocations, user) {
    const groundSpeedMps = Math.hypot(message.vel_n_mps, message.vel_e_mps);
    const groundSpeedKn = ConvertMpsToKnots(groundSpeedMps);

    const altMslFt = ConvertMetersToFeet(message.alt_msl_m);
    const altHaeFt = ConvertMetersToFeet(message.alt_hae_m);
    const altAglFt = ConvertMetersToFeet(message.alt_agl_m);

    const climbRateFpm = ConvertMpsToFpm(-message.vel_d_mps);

    const updatedMessage = {
        ...message,
        ground_speed_mps: groundSpeedMps,
        ground_speed_kn: groundSpeedKn,
        rotation: -message.course_deg,
        tail_number: message.call_sign,
        alt_msl_ft: altMslFt,
        alt_hae_ft: altHaeFt,
        alt_agl_ft: altAglFt,
        climb_rate_fpm: climbRateFpm,
        history_locations: previousAsdLocations.get(message.id) || [],
        show_dpz: {
            0: true,
            [user.id]: message.source_type === "TELEMETRY" ? true : false
        }
    };
    return updatedMessage;
}
